// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useFilteredNav Hook
// Filters NavItem[] by permission level and role slug
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { usePermission } from './usePermission';
import { useRoleStore } from '../store/roleStore';
import { hasMinLevel } from '../types/rbac';
import type { ModuleId } from '../types/rbac';
import type { NavItem } from '../types/nav';

export function useFilteredNav(moduleId: ModuleId, items: NavItem[]): NavItem[] {
  const access = usePermission(moduleId);
  const myRole = useRoleStore(s => s.myRole);
  const demoRole = useRoleStore(s => s.demoRoleOverride);
  const effectiveRole = demoRole ?? myRole;

  return useMemo(() => {
    if (effectiveRole?.roleSlug === 'super_admin' || effectiveRole?.roleSlug === 'eoc_director') {
      return items;
    }

    const slug = effectiveRole?.roleSlug ?? '';

    return items.filter(item => {
      // Check minLevel
      if (item.minLevel && !hasMinLevel(access.level, item.minLevel)) {
        return false;
      }

      // Check whitelist
      if (item.visibleToSlugs && item.visibleToSlugs.length > 0) {
        if (!item.visibleToSlugs.includes(slug)) return false;
      }

      // Check blacklist
      if (item.hiddenFromSlugs && item.hiddenFromSlugs.length > 0) {
        if (item.hiddenFromSlugs.includes(slug)) return false;
      }

      return true;
    });
  }, [items, access.level, effectiveRole?.roleSlug]);
}
